import React, { Component } from 'react';
import { useLocation, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import jsPDF from 'jspdf'; // Make sure this import exists

import qrService from '../services/qrService';
import keyService from '../services/keyService';
import NavigationBar from './NavigationBar';
import LoadEncryptionKeySection from './LoadEncryptionKeySection';
import Footer from './Footer';
import { useTranslation } from '../i18n';
import './NewTextboxResult.css'; // Create this CSS file next

class NewTextboxResult extends Component {
  constructor(props) {
    super(props);
    this.state = {
      emailsArray: [],
      messageSaved: !!props.messageId, // Track if the message was already saved
      keySaved: !!props.keyId || !!props.useExistingKey, // Mark as saved if using existing key
      showEncryptedText: false,
      // The key QR is the single artifact without which the paper is worthless,
      // so it is visible by default (the user can still collapse it).
      showEncryptionKey: true,
      copySuccess: false,
      copyKeySuccess: false, // Add new state for encryption key copy success
    };
    this.CHARACTERLIMITS = 156;
    this.canvases = [];
    this.downloadCanvas = null; // Initialize downloadCanvas reference
    this.howManyCodes = 1;
    this.onHandleDownloadClick = this.onHandleDownloadClick.bind(this);
    this.onHandleDownloadPdfClick = this.onHandleDownloadPdfClick.bind(this);
    this.saveEncryptedMessage = this.saveEncryptedMessage.bind(this);
    this.saveEncryptionKey = this.saveEncryptionKey.bind(this);
    this.copyToClipboard = this.copyToClipboard.bind(this);
    this.copyKeyToClipboard = this.copyKeyToClipboard.bind(this);
  }

  async componentDidMount() {
    const encryptedText = this.props.encryptedText || '';
    await this.generateCanvasCode(this.howManyCodes, encryptedText);
    
    // Only save the key if we're creating a new key (not reusing an existing one).
    // In passphrase mode there is no key at all - nothing is stored anywhere.
    if (this.props.encryptionKey && !this.state.keySaved && !this.props.useExistingKey && !this.props.isPassphrase) {
      await this.saveEncryptionKey();
    }
  }

  async saveEncryptionKey() {
    const { t } = this.props;
    try {
      this.setState({ isSaving: true });

      if (this.props.isLoggedIn) {
        // For logged-in users, use the full upload method
        const { message, key } = await keyService.upload({
          encryptionKey: this.props.encryptionKey,
          subject: this.props.subject || t('result.untitledMessage'),
          name: t('result.keyForName', { subject: this.props.subject || t('result.untitledMessage') })
        });

        this.setState({
          messageSaved: true,
          keySaved: true,
          messageId: message.id,
          keyId: key.id,
          isSaving: false,
        });

        this.showSuccessNotification(t('result.notifSavedAccount'));
      } else {
        // For non-logged-in users, save key locally
        const savedKey = await keyService.createKey({
          encryptionKey: this.props.encryptionKey,
          name: t('result.keyForName', { subject: this.props.subject || t('result.untitledMessage') }),
          subject: this.props.subject
        });

        this.setState({
          keySaved: true,
          keyId: savedKey.id,
          isSaving: false,
        });

        this.showSuccessNotification(t('result.notifKeySavedLocally'));
      }
    } catch (error) {
      console.error("Error saving encryption key:", error);
      this.setState({ isSaving: false });
    }
  }

  async saveEncryptedMessage() {
    // This method is now only for logged-in users to save messages
    if (!this.props.isLoggedIn) {
      return;
    }
    
    try {
      this.setState({ isSaving: true });
      const { t } = this.props;
      const { message, key } = await keyService.upload({
        encryptionKey: this.props.encryptionKey,
        subject: this.props.subject || t('result.untitledMessage'),
        name: t('result.keyForName', { subject: this.props.subject || t('result.untitledMessage') })
      });

      this.setState({
        messageSaved: true,
        keySaved: true,
        messageId: message.id,
        keyId: key.id,
        isSaving: false,
      });

      this.showSuccessNotification(t('result.notifMessageSaved'));
    } catch (error) {
      console.error("Error saving encrypted message:", error);
      this.setState({ isSaving: false });
    }
  }

  showSuccessNotification(message) {
    // Create temporary notification
    const notification = document.createElement('div');
    notification.className = 'success-notification';
    notification.innerHTML = `<i class="fa fa-check-circle"></i> ${message}`;
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => document.body.removeChild(notification), 500);
    }, 3000);
  }

  onHandleDownloadClick(e) {
    e.preventDefault();
    const anchor = document.getElementById('download');
    anchor.href = this.joinCanvasses().dataURL;
    // Create a user-friendly filename with subject and descriptive text
    const safeSubject = (this.props.subject || 'SecurePaper').replace(/[^a-z0-9]/gi, '-').substring(0, 30);
    anchor.download = `${safeSubject}-SecurePaper-Code-${moment().format('DD-MM-YYYY-HHmmss')}.png`;
    anchor.click();

    this.showSuccessNotification(this.props.t('result.notifDownloaded'));
  }

  onHandleDownloadPdfClick(e) {
    e.preventDefault();
    const qrCodeData = this.joinCanvasses();
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm' });

    const PAGE_W = 210;
    const PAGE_H = 297;
    const MARGIN = 18;
    const NAVY = [44, 62, 80];
    const BLUE = [37, 99, 235];
    const GRAY = [107, 122, 137];
    const LIGHT = [223, 230, 236];

    // ── header band ──
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, PAGE_W, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('SecurePaper', MARGIN, 10.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Encrypted paper backup', PAGE_W - MARGIN, 10.5, { align: 'right' });

    // ── title + meta ──
    const title = this.props.subject || 'Encrypted document';
    doc.setTextColor(...NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text(title, PAGE_W / 2, 30, { align: 'center', maxWidth: PAGE_W - 2 * MARGIN });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    const partsLabel = this.howManyCodes > 1 ? ` · ${this.howManyCodes} QR codes (scan all of them)` : '';
    const modeLabel = this.props.isPassphrase ? 'passphrase-protected' : 'key-protected';
    doc.text(`Created ${moment().format('DD MMM YYYY, HH:mm')} · AES-256 · ${modeLabel}${partsLabel}`, PAGE_W / 2, 37, { align: 'center' });

    // ── QR image, framed and scaled to fit ──
    const ratio = qrCodeData.height / qrCodeData.width;
    let imageWidth = this.howManyCodes > 1 ? 120 : 60;
    let imageHeight = imageWidth * ratio;
    const maxImageHeight = 130;
    if (imageHeight > maxImageHeight) {
      imageHeight = maxImageHeight;
      imageWidth = imageHeight / ratio;
    }
    const imgX = (PAGE_W - imageWidth) / 2;
    const imgY = 44;
    doc.setDrawColor(...LIGHT);
    doc.setLineWidth(0.4);
    doc.roundedRect(imgX - 4, imgY - 4, imageWidth + 8, imageHeight + 8, 2, 2, 'S');
    doc.addImage(qrCodeData.dataURL, 'PNG', imgX, imgY, imageWidth, imageHeight);

    let y = imgY + imageHeight + 14;

    // ── "how to read" box ──
    const boxWidth = PAGE_W - 2 * MARGIN;
    const steps = [
      '1.  Go to https://app.securepaper.app/decrypt (works on any phone or computer)',
      `2.  Scan ${this.howManyCodes > 1 ? `all ${this.howManyCodes} codes above (any order)` : 'the code above'} with the camera, or photograph and upload ${this.howManyCodes > 1 ? 'them' : 'it'}`,
      this.props.isPassphrase
        ? '3.  Enter the passphrase chosen when this document was created - nothing else is needed'
        : '3.  Provide the encryption key (saved keys are tried automatically; or scan/paste the key)',
    ];
    doc.setDrawColor(...LIGHT);
    doc.roundedRect(MARGIN, y, boxWidth, 30, 2, 2, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text('How to read this document', MARGIN + 5, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    steps.forEach((step, i) => doc.text(step, MARGIN + 5, y + 13.5 + i * 5.5, { maxWidth: boxWidth - 10 }));
    y += 36;

    // ── "to whoever finds this" box ──
    doc.setFillColor(246, 249, 252);
    doc.roundedRect(MARGIN, y, boxWidth, 17, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...NAVY);
    doc.text('To whoever finds this paper', MARGIN + 5, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY);
    doc.text(
      `This is an AES-256 encrypted document. Without the ${this.props.isPassphrase ? 'passphrase' : 'encryption key'} its content cannot be read.`,
      MARGIN + 5, y + 12, { maxWidth: boxWidth - 10 }
    );
    y += 23;

    // ── emergency note ──
    doc.setFontSize(8.5);
    doc.setTextColor(...BLUE);
    doc.text('Emergency: an offline decryptor that works without our servers:  https://app.securepaper.app/offline-decryptor.html', MARGIN, y, { maxWidth: boxWidth });
    doc.setTextColor(...GRAY);
    doc.text('Printing & storage guide:  https://securepaper.app/guide', MARGIN, y + 5);

    // ── footer ──
    doc.setDrawColor(...LIGHT);
    doc.line(MARGIN, PAGE_H - 12, PAGE_W - MARGIN, PAGE_H - 12);
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('Created with SecurePaper', MARGIN, PAGE_H - 7);
    doc.text('securepaper.app', PAGE_W - MARGIN, PAGE_H - 7, { align: 'right' });

    const safeSubject = (this.props.subject || 'SecurePaper').replace(/[^a-z0-9]/gi, '-').substring(0, 30);
    doc.save(`${safeSubject}-SecurePaper-Document-${moment().format('DD-MM-YYYY-HHmmss')}.pdf`);

    this.showSuccessNotification(this.props.t('result.notifPdfCreated'));
  }

  joinCanvasses() {
    const columnsAndRows = this.howManyCodes > 4 ? 4 : this.howManyCodes;
    const maxRows = Math.ceil(this.howManyCodes / columnsAndRows);
    // Make sure downloadCanvas is properly referenced
    return qrService.joinCanvasesToDataUrl(this.downloadCanvas, this.canvases.slice(0, this.howManyCodes), columnsAndRows, maxRows);
  }

  async generateCanvasCode(howManyCodes, encryptedText) {
    const generatedCanvases = await qrService.generateQrCodesWithText(this.canvases.slice(0, howManyCodes), encryptedText, this.CHARACTERLIMITS);
    generatedCanvases.forEach((e, i) => {
      this.canvases[i] = e;
    });
  }

  renderCanvasCodes(howManyCodes) {
    const canvasElements = [];
    for (let i = 0; i < howManyCodes; i += 1) {
      canvasElements.push((
        <div className="qrcode-item" key={`canvas-${i}`}>
          <canvas ref={(c) => { this.canvases[i] = c; }} />
        </div>
      ));
    }

    return canvasElements;
  }

  copyToClipboard() {
    if (navigator.clipboard && this.props.encryptedText) {
      navigator.clipboard.writeText(this.props.encryptedText)
        .then(() => {
          this.setState({ copySuccess: true });
          setTimeout(() => this.setState({ copySuccess: false }), 2000);
        })
        .catch(err => console.error('Failed to copy text: ', err));
    }
  }

  copyKeyToClipboard() {
    if (navigator.clipboard && this.props.encryptionKey) {
      navigator.clipboard.writeText(this.props.encryptionKey)
        .then(() => {
          this.setState({ copyKeySuccess: true });
          setTimeout(() => this.setState({ copyKeySuccess: false }), 2000);
        })
        .catch(err => console.error('Failed to copy key: ', err));
    }
  }

  render() {
    const { t } = this.props;
    const encryptedText = this.props.encryptedText || '';
    this.howManyCodes = Math.ceil(encryptedText.length / this.CHARACTERLIMITS) || 1;
    this.codesHorizontal = this.howManyCodes > 2;

    return (
      [
        <NavigationBar key="navbar" />,
        <div className="container result-container" key="container">
          <div className="result-header">
            <h1 className="text-center">{t('result.ready')}</h1>
            <p className="text-center text-muted">{t('result.readySubtitle')}</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-md-10">
              <div className="result-card">
                <div className="result-progress">
                  <div className="progress-step completed">
                    <div className="step-number"><i className="fa fa-check" aria-hidden="true"></i></div>
                    <div className="step-label">{t('result.stepInfoEntered')}</div>
                  </div>
                  <div className="progress-line completed"></div>
                  <div className="progress-step active">
                    <div className="step-number">2</div>
                    <div className="step-label">{t('result.stepGenerated')}</div>
                  </div>
                </div>

                {/* SecurePaper Section - updated to match other sections */}
                <div className="section-container">
                  <div className="section-header">
                    <h3><i className="fa fa-qrcode" aria-hidden="true"></i> {t('result.yourSecurePaper')}</h3>
                  </div>
                  
                  <div className="section-content">
        
                                        <div className="qr-container">
                      <div ref={elem => this.qrCodeContainer = elem} className="qr-codes horizontal">
                        {this.renderCanvasCodes(this.howManyCodes)}
                      </div>
                    </div>
                    <div className="qr-actions">
                      <button onClick={this.onHandleDownloadClick} className="btn btn-outline-primary">
                        <i className="fa fa-download" aria-hidden="true"></i> {t('result.download')}
                      </button>
                      <button onClick={this.onHandleDownloadPdfClick} className="btn btn-outline-primary ml-2">
                        <i className="fa fa-print" aria-hidden="true"></i> {t('result.print')}
                      </button>
                      <a id="download" style={{ display: 'none' }}>Hidden download link</a>
                      {/* Add a hidden canvas for downloading */}
                      <canvas 
                        ref={(c) => { this.downloadCanvas = c; }} 
                        style={{ display: 'none' }}
                      />
                    </div>
                    <br />
                    <p className="qr-instructions">
                      {t('result.howToAccess')}
                    </p>
                    <ul className="qr-steps">
                      <li><i className="fa fa-mobile" aria-hidden="true"></i> <Link to="/decrypt">{t('result.goDecrypt')}</Link></li>
                      <li><i className="fa fa-camera" aria-hidden="true"></i> {t('result.scanPrinted')}</li>
                      <li><i className="fa fa-key" aria-hidden="true"></i> {this.props.isPassphrase
                        ? t('result.passphraseStep')
                        : (this.props.isLoggedIn ?
                          t('result.loggedInKeysStep') :
                          t('result.localKeysStep'))
                      }</li>
                      <li><i className="fa fa-life-ring" aria-hidden="true"></i> {t('result.emergencyBackup')} <a href="/offline-decryptor.html" download>{t('result.downloadOfflineDecryptor')}</a> {t('result.offlineDecryptorDesc')}</li>
                      <li><i className="fa fa-book" aria-hidden="true"></i> <a href="https://securepaper.app/guide" target="_blank" rel="noopener noreferrer">{t('result.printingGuideLink')}</a></li>
                    </ul>

                    {this.props.isPassphrase && (
                      <div className="alert alert-success mt-2 mb-3">
                        <i className="fa fa-user-secret" aria-hidden="true"></i> {t('result.passphraseAlert1')}
                        <strong> {t('result.passphraseAlertStrong')}</strong> {t('result.passphraseAlert2')}
                      </div>
                    )}

                    {!this.props.isPassphrase && !this.props.isLoggedIn && (
                      <div className="alert alert-warning mt-2 mb-3">
                        <i className="fa fa-exclamation-triangle" aria-hidden="true"></i> {t('result.notLoggedInWarning')}{' '}
                        <strong>{t('result.notLoggedInWarningStrong')}</strong>
                      </div>
                    )}
                    
                    {!this.props.isPassphrase && this.props.isLoggedIn && this.state.messageSaved && (
                      <div className="alert alert-success mt-2 mb-3">
                        <i className="fa fa-check-circle" aria-hidden="true"></i>
                        {this.props.useExistingKey
                          ? t('result.usingExistingAccount')
                          : t('result.keySavedAccount')
                        }
                      </div>
                    )}

                    {!this.props.isPassphrase && !this.props.isLoggedIn && this.state.keySaved && (
                      <div className="alert alert-info mt-2 mb-3">
                        <i className="fa fa-info-circle" aria-hidden="true"></i>
                        {this.props.useExistingKey
                          ? t('result.usingExistingLocal')
                          : t('result.keySavedLocal')
                        }
                        {!this.props.useExistingKey && (
                          <>
                            {" "}
                            <Link to="/signup" className="alert-link">{t('result.signupLink')}</Link> {t('result.signupToSync')}
                          </>
                        )}
                      </div>
                    )}
                    

                  
                  </div>
                </div>
                
                {/* Encryption Key Section - hidden entirely in passphrase mode (there is no key) */}
                {!this.props.isPassphrase && (
                <div className="section-container">
                  <div className="section-header" onClick={() => this.setState({ showEncryptionKey: !this.state.showEncryptionKey })}>
                    <h3><i className="fa fa-key" aria-hidden="true"></i> {t('result.encryptionKeySection')}</h3>
                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.setState({ showEncryptionKey: !this.state.showEncryptionKey });
                      }}
                      aria-expanded={this.state.showEncryptionKey}
                      aria-controls="encryption-key-content"
                    >
                      {this.state.showEncryptionKey ? t('result.hide') : t('result.show')}
                    </button>
                  </div>
                  
                  {this.state.showEncryptionKey && (
                    <div className="section-content" id="encryption-key-content">

                      <div className="key-actions">
                        <button
                          onClick={this.copyKeyToClipboard}
                          className={`btn btn-sm ${this.state.copyKeySuccess ? 'btn-success' : 'btn-outline-primary'}`}
                          title={t('result.copyKeyTitle')}
                        >
                          {this.state.copyKeySuccess ? (
                            <><i className="fa fa-check" aria-hidden="true"></i> {t('result.copiedKey')}</>
                          ) : (
                            <><i className="fa fa-copy" aria-hidden="true"></i> {t('result.copyKey')}</>
                          )}
                        </button>

                        {!this.state.keySaved && (
                          <button 
                            onClick={this.saveEncryptionKey} 
                            className="btn btn-sm btn-outline-success ml-2"
                            disabled={this.state.isSaving}
                          >
                            {this.state.isSaving ? (
                              <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('result.saving')}</>
                            ) : (
                              <><i className="fa fa-save" aria-hidden="true"></i> {this.props.isLoggedIn ? t('result.saveToAccount') : t('result.saveLocally')}</>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {this.state.keySaved && (
                        <div className="save-success mt-2">
                          <i className="fa fa-check-circle" aria-hidden="true"></i>
                          {this.props.useExistingKey
                            ? (this.props.isLoggedIn ? t('result.statusUsingExistingAccount') : t('result.statusUsingExistingLocal'))
                            : (this.props.isLoggedIn ? t('result.statusSavedAccount') : t('result.statusSavedLocal'))
                          }
                        </div>
                      )}
                      
                      <div className="key-qr-code">
                        <div className="key-content">
                          <LoadEncryptionKeySection 
                            {...this.props} 
                            {...this.state} 
                            encryptionKey={this.props.encryptionKey}
                            messageId={this.state.messageId || this.props.messageId}
                            keyId={this.state.keyId || this.props.keyId}
                          />
                        </div>
                      </div>
                    </div>
                  )}


                </div>
                )}

                <div className="result-actions">
                  <Link to="/create_encrypted_text" className="btn btn-primary">
                    <i className="fa fa-plus" aria-hidden="true"></i> {t('result.createAnother')}
                  </Link>
                  <Link to="/" className="btn btn-outline-secondary">
                    <i className="fa fa-home" aria-hidden="true"></i> {t('result.goToDashboard')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>,
        <Footer key="footer" />
      ]
    );
  }
}

NewTextboxResult.propTypes = {
  encryptedText: PropTypes.string,
  encryptionKey: PropTypes.string,
  subject: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  messageId: PropTypes.string,
  keyId: PropTypes.string,
  useExistingKey: PropTypes.bool,
  isPassphrase: PropTypes.bool,
  t: PropTypes.func,
};

// Wrapper to get location state from react-router and the translation function
const NewTextboxResultWithLocation = (props) => {
  const location = useLocation();
  const { t } = useTranslation();
  return <NewTextboxResult {...props} {...location.state} t={t} />;
};

export default NewTextboxResultWithLocation;
